import { useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/router';
import useCourseInfo from '@api/hooks/course/useCourseInfo';
import useClassInfo from '@api/hooks/class/useClassInfo';
import useSimilarAndOtherCourses from '@api/hooks/class/useSimilarAndOtherCourses';
import useCheckFeatureLimit from '@cont/DeviceLimitation/useCheckFeatureLimit';
import useCourseClassPlayer from '@hooks/useCourseClassPlayer';
import { FeatureType } from 'types/features';
import { isUpcoming } from '@models/Item';
import { readCookie } from '@utils/cookies';
import getParamsFromURL from '@utils/getParamsFromURL';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { emptyObject } from '@constants/empty';
import { useIsMobile } from '@stores/AppCommonData';
import {
  usePrimeQuotaStore,
  PrimeMode,
  UserPlatformPlan
} from '@stores/prime-quota/';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn, getIsPlatformUnlocked } from '@api/selectors/auth';

import useTopology from '@api/hooks/topology/useTopology';
import { getIsPrimeEnabled } from '@api/selectors/topology';
import usePlusIconicSubscribed from '@hooks/usePlusIconicSubscribed';
import usePrimeTrialPromoModalStore from '@stores/prime-pitch/store';
import { PrimeTrialPromoContextualId } from '@stores/prime-pitch/types';

const useLanding = () => {
  const { query } = useRouter();
  const { uid: classUID } = query;

  const { classInfo, mutate: mutateClassData } = useClassInfo(classUID);
  const { course: courseInfo, mutate: mutateCourseData } = useCourseInfo(
    classInfo.programme.uid
  );
  const { mutate: mutateSimilarCourses } = useSimilarAndOtherCourses(classUID);

  const isMobile = useIsMobile();
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const isPlatformUnlocked = useMyInfo(getIsPlatformUnlocked);
  const { isValidating: fetchingUserData } = useMyInfo();

  const { open: openClassPlayer } = useCourseClassPlayer();
  const handleClassItemClick = useCheckFeatureLimit(
    FeatureType.CLASS,
    (...args) => openClassPlayer(...args)
  );

  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const { closeElement: closeClassPlayer } = useToggleable(
    'COURSE_CLASS_PLAYER'
  );

  const goalUID = courseInfo?.goal?.uid;
  const { topology: goalInfo } = useTopology(goalUID);
  const goalSlug = goalInfo?.slug;
  const isPrimeEnabled = getIsPrimeEnabled(goalInfo);
  const isPlusIconicUser = usePlusIconicSubscribed({ goalUID });
  const consumeQuota = usePrimeQuotaStore((state) => state.consumeQuota);
  const fetchQuotaDetails = usePrimeQuotaStore(
    (state) => state.fetchQuotaDetails
  );
  const getQuotaForMode = usePrimeQuotaStore((state) => state.getQuotaForMode);
  const fetchUnlockedVideosUids = usePrimeQuotaStore(
    (state) => state.fetchUnlockedVideosUids
  );
  const openPrimeTrialPromoModal = usePrimeTrialPromoModalStore(
    (state) => state.openModal
  );
  const closePrimeTrialPromoModal = usePrimeTrialPromoModalStore(
    (state) => state.closeModal
  );
  const isPrimeModalOpen = usePrimeTrialPromoModalStore((state) => state.open);

  const router = useRouter();
  const didWeOpenModal = useRef(false);
  const previousModalOpenState = useRef(false);
  const lastCheckedClassUID = useRef(null);

  const handleBackClick = useCallback(() => router.back(), [router]);

  useEffect(() => {
    if (
      lastCheckedClassUID.current !== classUID &&
      lastCheckedClassUID.current !== null
    ) {
      if (isPrimeModalOpen && didWeOpenModal.current) {
        closePrimeTrialPromoModal();
      }
      didWeOpenModal.current = false;
      lastCheckedClassUID.current = classUID;
    } else if (lastCheckedClassUID.current === null) {
      lastCheckedClassUID.current = classUID;
    }
  }, [classUID, isPrimeModalOpen, closePrimeTrialPromoModal]);
  useEffect(() => {
    if (
      previousModalOpenState.current &&
      !isPrimeModalOpen &&
      didWeOpenModal.current
    ) {
      if (lastCheckedClassUID.current === classUID) {
        didWeOpenModal.current = false;
        handleBackClick();
      }
    }
    previousModalOpenState.current = isPrimeModalOpen;
  }, [isPrimeModalOpen, classUID, handleBackClick]);

  useEffect(() => {
    if (!courseInfo.uid) {
      if (!readCookie('accessToken') && !fetchingUserData) {
        openLoginDrawer();
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      const [isSyllabusFeedParam, noAutoplayParam] = getParamsFromURL(
        window.location.href,
        ['isSyllabusFeed', 'noAutoplay']
      );

      const isFreeLesson = isSyllabusFeedParam === 'true';
      const noAutoplay = noAutoplayParam === 'true';

      const fetchAndCheckForPlayable = async () => {
        const [updatedClassInfo, updatedCourseInfo] = await Promise.all([
          mutateClassData(),
          mutateCourseData()
        ]);
        const { programme = emptyObject } = updatedCourseInfo || emptyObject;
        const { isSpecial = false } = programme;

        if (
          !isMobile &&
          !noAutoplay &&
          (!isSpecial ||
            (isSpecial && !isUpcoming(updatedClassInfo) && isPlatformUnlocked))
        ) {
          const accessToken = readCookie('accessToken');

          if (isSpecial && isPrimeEnabled && !isPlusIconicUser && goalUID) {
            const [quotaResponse, unlockedVideos] = await Promise.all([
              fetchQuotaDetails({ goalUID, accessToken }),
              fetchUnlockedVideosUids({ goalUID, accessToken })
            ]);

            const isCurrentClassUnlocked =
              !!classUID && unlockedVideos && unlockedVideos.includes(classUID);

            if (isCurrentClassUnlocked) {
              handleClassItemClick({
                lesson: updatedClassInfo,
                course: updatedCourseInfo,
                byPassCheck: isFreeLesson,
                isFreeLesson,
                forceFetch: isFreeLesson
              });
              if (isSpecial) {
                mutateSimilarCourses();
              }
              return;
            }

            if (
              quotaResponse &&
              quotaResponse.userPlatformPlan === UserPlatformPlan.FREE
            ) {
              const currentQuota = getQuotaForMode(PrimeMode.VIDEO);

              if (currentQuota.isQuotaExhausted) {
                closeClassPlayer();

                didWeOpenModal.current = true;
                openPrimeTrialPromoModal(
                  PrimeTrialPromoContextualId.VIDEO,
                  goalUID,
                  goalSlug
                );
                return;
              } // Await quota consumption before allowing playback

              await consumeQuota({
                goalUID,
                objectUID: classUID,
                mode: PrimeMode.VIDEO,
                accessToken
              });
            }
          }

          handleClassItemClick({
            lesson: updatedClassInfo,
            course: updatedCourseInfo,
            byPassCheck: isFreeLesson,
            isFreeLesson,
            forceFetch: isFreeLesson
          });
        }

        if (isSpecial) {
          mutateSimilarCourses();
        }
      };

      fetchAndCheckForPlayable();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, classInfo.uid]);
};

export default useLanding;
